import React, { useContext, useState, useEffect } from 'react';
import {
  arrayOf,
  number,
  shape,
  bool,
  string
} from 'prop-types';
import { ExperienceContext, useStore } from '@thd-nucleus/experience-context';
import useAxios from 'axios-hooks';
import {
  useDataModel, string as stringType, shape as shapeType, arrayOf as arrayOfType, alias, params, bool as boolType
} from '@thd-nucleus/data-sources';
import { InstantCheckoutEdit } from './subcomponents/InstantCheckoutEdit';
import { InstantCheckoutEnabled } from './subcomponents/InstantCheckoutEnabled';
import { InstantCheckoutEnroll } from './subcomponents/InstantCheckoutEnroll';
import { InstantCheckoutSignin } from './subcomponents/InstantCheckoutSignin';
import * as helpers from './InstantCheckoutHelper';
import { track } from './analytics/instant-checkout-analytics';
import './instant-checkout.scss';

const INSTANT_CHECKOUT_URL = '/p/svcs/instantCheckoutModel';

const useUserDetails = ({ itemId, storeId }) => {
  const [userDetails, setUserDetails] = useState({
    ICTemplate: '',
    addressCity: '',
    addressZipCode: '',
    xref: '',
    displayCardBrand: ''
  });

  const productOptions = {
    skip: storeId === '8119',
    ssr: false,
    variables: {
      itemId,
      storeId
    }
  };

  const { data, loading, error } = useDataModel('clientOnlyProduct', productOptions);
  const [ICResponse, fetchInstantCheckout] = useAxios({ url: INSTANT_CHECKOUT_URL }, { manual: true });
  const {
    fulfillment = {}
  } = data?.product || {};

  useEffect(() => {
    if (data && fulfillment) {
      if (helpers.isInstantCheckoutEligible(fulfillment)) {
        fetchInstantCheckout();
      } else if (helpers.isGuestInstantCheckoutEligible(fulfillment)) {
        track({ ICTemplate: 'DEFAULT' });
        setUserDetails({
          ...userDetails,
          ICTemplate: 'DEFAULT'
        });
      }
    }
  }, [data]);
  useEffect(() => {
    if (ICResponse.data && !ICResponse.error) {
      const ICModel = ICResponse?.data?.instantCheckoutModel;
      track({ ICTemplate: ICModel?.displayOption });
      setUserDetails({
        ...userDetails,
        ICTemplate: ICModel?.displayOption,
        addressCity: ICModel?.addressCity,
        addressZipCode: ICModel?.addressZipCode,
        xref: ICModel?.xref,
        displayCardBrand: ICModel?.displayCardBrand
      });
    }
  }, [ICResponse]);
  return [userDetails];
};

const InstantCheckout = ({
  itemId,
  quantity,
  fulfillmentMethod,
  productAddOns,
  hideInvitation
}) => {

  const {
    channel,
  } = useContext(ExperienceContext);

  const { storeId } = useStore();

  const [userDetails] = useUserDetails({ itemId, storeId });

  if (userDetails?.ICTemplate === 'IC') {
    return (
      <InstantCheckoutEnabled
        atcRequest={helpers.getInstantCheckoutAtcRequest({ itemId, quantity, productAddOns })}
        instantCheckoutModel={userDetails}
        channel={channel}
        productAddOns={productAddOns}
        fulfillmentMethod={fulfillmentMethod}
      />
    );
  }

  if (hideInvitation) {
    return null;
  }

  if (userDetails?.ICTemplate === 'ENROLL' || userDetails?.ICTemplate === 'DEFAULT') {
    return (<InstantCheckoutEnroll channel={channel} />);
  }
  if (userDetails?.ICTemplate === 'SIGN_IN') {
    return (<InstantCheckoutSignin />);
  }
  if (userDetails?.ICTemplate === 'EDIT_IC') {
    return (<InstantCheckoutEdit />);
  }
  return null;
};

InstantCheckout.propTypes = {
  itemId: string,
  quantity: number,
  fulfillmentMethod: string,
  hideInvitation: bool,
  productAddOns: shape({
    attachedLabor: arrayOf(shape({
      attachedLaborSku: string
    })),
    warrantyItem: shape({
      itemId: string
    }),
    service: shape({
      type: string,
      selected: bool,
      category: string
    })
  })
};

InstantCheckout.defaultProps = {
  itemId: null,
  quantity: 1,
  fulfillmentMethod: null,
  productAddOns: null,
  hideInvitation: null
};

InstantCheckout.dataModel = {
  clientOnlyProduct: alias('product').params({ itemId: stringType().isRequired() }).shape({
    itemId: stringType(),
    dataSources: stringType(),
    fulfillment: params({ storeId: stringType() }).shape({
      fulfillmentOptions: arrayOfType(shapeType({
        type: stringType(),
        services: arrayOfType(shapeType({
          type: stringType(),
          locations: arrayOfType(shapeType({
            inventory: shapeType({
              isInStock: boolType()
            })
          }))
        }))
      }))
    }),
  })
};

export { InstantCheckout };
